import { useAppSelector } from '../../../../hooks/useAppSelector';
import * as monitoringSlice from '../../../../data/monitoring.slice';
import { Box, Stack, styled } from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  forwardRef,
  memo,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { isEqual, isNil } from 'lodash';
import { useAppDispatch } from '../../../../hooks/useAppDispatch';
import {
  MemoizedWorkflowFlowChart,
  WorkflowChartItem,
  WorkflowTransition,
} from '../WorkflowFlowChart';
import SimplifiedWorkflowStep, {
  SimplifiedWorkflowStepRef,
} from './SimplifiedWorkflowStep';
import {
  SendMessageActionFormData,
  SimplifiedWorkflowStepFormData,
} from '../../../../data/models/simplified-workflow.model';
import simplifiedWorkflowUtils, {
  CreateSimplifiedWorkflowData,
} from '../../../../services/simplifiedWorkflowUtils';
import describeSimplifiedWorkflowUtils from '../../../../services/describeSimplifiedWorkflowUtils';
import * as uiSlice from '../../../../data/ui.slice';
import * as lookupSlice from '../../../../data/lookup.slice';
import workflowChartUtils from '../../../../services/workflowChartUtils';

export interface EditSimplifiedWorkflowRef {
  saveWorkflow: () => void;
}

interface EditSimplifiedWorkflowProps {
  isCreate: boolean;
  workQueueUUID: string;
  existingWorkflow?: CreateSimplifiedWorkflowData;
}

const PREFIX = 'EditSimplifiedWorkflow';

const classes = {
  container: `${PREFIX}-container`,
  chartContainer: `${PREFIX}-chartContainer`,
  chart: `${PREFIX}-chart`,
  step: `${PREFIX}-step`,
};

const StyledBox = styled(Box)(({ theme }) => ({
  [`& .${classes.container}`]: {
    paddingTop: theme.spacing(3),
    paddingBottom: theme.spacing(3),
  },
  [`& .${classes.chartContainer}`]: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  [`& .${classes.chart}`]: {
    width: 600,
  },
  [`& .${classes.step}`]: {
    flex: 1,
    maxWidth: 650,
  },
}));

const EditSimplifiedWorkflow = forwardRef(
  (
    { isCreate, existingWorkflow, workQueueUUID }: EditSimplifiedWorkflowProps,
    ref,
  ) => {
    const { t } = useTranslation('common');
    const dispatch = useAppDispatch();
    useImperativeHandle(ref, () => ({ saveWorkflow }));
    const [workflowSteps, setWorkflowSteps] = useState<
      SimplifiedWorkflowStepFormData[]
    >([]);
    const [draftStep, setDraftStep] = useState<
      SimplifiedWorkflowStepFormData | undefined
    >(undefined);
    const [selectedWorkflowId, setSelectedWorkflowId] = useState('');
    const [nextSelectedWorkflowId, setNextSelectedWorkflowId] = useState('');
    const [showAddStepButton, setShowAddStepButton] = useState(true);
    const [removedChartItemId, setRemovedChartItemId] = useState<
      string | undefined
    >();
    const smartLabels = useAppSelector(
      lookupSlice.selectCommsCenterSmartLabels,
    );
    const workflowDraft = useAppSelector(monitoringSlice.selectDraftWorkflow);
    const systemEvents = useAppSelector(monitoringSlice.selectSystemEvents);
    const workflowStepRef = useRef<SimplifiedWorkflowStepRef>();

    useEffect(() => {
      dispatch(monitoringSlice.fetchSystemEventsAsync());
    }, []);

    useEffect(() => {
      if (!isCreate && existingWorkflow && workflowSteps.length === 0) {
        setWorkflowSteps(existingWorkflow.steps);
        //setSelectedWorkflowIndex(0);
        if (existingWorkflow.steps.length > 0) {
          setNextSelectedWorkflowId(existingWorkflow.steps[0].tempId ?? '');
        }

        setShowAddStepButton(false);
      }
    }, [isCreate, existingWorkflow, workflowSteps]);

    useEffect(() => {
      // Need another state to make sure we dont set the new workflow
      // until the next render after form reset.
      if (nextSelectedWorkflowId) {
        setSelectedWorkflowId(nextSelectedWorkflowId);
      }
    }, [nextSelectedWorkflowId]);

    useEffect(() => {
      if (isCreate && workflowSteps.length === 0) {
        const startStep = simplifiedWorkflowUtils.getDefaultTriggerStep(t);
        setWorkflowSteps([startStep]);
        setSelectedWorkflowId(startStep.tempId ?? '');
      }
    }, [isCreate, workflowSteps]);

    const saveCurrentStep = (
      params:
        | { addNewStep: false; nextId?: string }
        | {
            addNewStep: true;
            parentId: string;
            branch?: string;
            nextId?: string;
          },
    ) => {
      const { addNewStep, nextId } = params;
      const currentStep = workflowStepRef.current?.getFormData();
      let nextSelectedWorkflowId = nextId;
      if (currentStep) {
        const updatedSteps = [...workflowSteps];
        const index = updatedSteps.findIndex(
          (step) => step.tempId === currentStep.tempId,
        );
        updatedSteps[index] = { ...currentStep };
        if (addNewStep) {
          updatedSteps.push(
            simplifiedWorkflowUtils.getDefaultActionStep(
              t,
              params.parentId,
              params.branch,
            ),
          );
          setDraftStep(undefined);
          setShowAddStepButton(false);
          nextSelectedWorkflowId = updatedSteps[updatedSteps.length - 1].tempId;
        }
        setWorkflowSteps(updatedSteps);
        workflowStepRef.current?.resetForm();

        if (nextSelectedWorkflowId) {
          // Need to another state to make sure we dont set the new workflow
          // until the next render after form reset.
          setTimeout(() => {
            setNextSelectedWorkflowId(nextSelectedWorkflowId!);
          }, 0);
        }
        return updatedSteps;
      }
      return workflowSteps;
    };

    const saveWorkflow = () => {
      if (workflowStepRef.current?.save()) {
        const steps = saveCurrentStep({ addNewStep: false });
        if (isCreate) {
          const workflowData: CreateSimplifiedWorkflowData = {
            workQueueUUID,
            name: workflowDraft?.name ?? '',
            description: workflowDraft?.description,
            steps,
          };
          try {
            const creatableWorkflow =
              simplifiedWorkflowUtils.createWorkflowFromForm(
                workflowData,
                smartLabels,
                systemEvents,
              );

            dispatch(monitoringSlice.createWorkflowAsync(creatableWorkflow));
          } catch (error: any) {
            dispatch(
              uiSlice.setErrorMessage(t('workflows.createWorkflowError')),
            );
          }
        } else {
          if (!existingWorkflow) {
            return;
          }
          const title = workflowDraft?.name ?? existingWorkflow?.name;
          const description =
            workflowDraft?.description ?? existingWorkflow?.description;
          const workflowData: CreateSimplifiedWorkflowData = {
            workQueueUUID: existingWorkflow.workQueueUUID,
            name: title ?? '',
            description,
            steps,
          };
          try {
            const creatableWorkflow =
              simplifiedWorkflowUtils.createWorkflowFromForm(
                workflowData,
                smartLabels,
                systemEvents,
              );
            const editableWorkflow = {
              ...creatableWorkflow,
              workflowUUID: existingWorkflow.workflowUUID!,
            };
            dispatch(monitoringSlice.updateWorkflowAsync(editableWorkflow));
          } catch (error: any) {
            dispatch(
              uiSlice.setErrorMessage(t('workflows.updateWorkflowError')),
            );
          }
        }
      }
    };

    const handleDeleteStep = (stepId: string) => {
      const stepToDelete = workflowSteps.find((step) => step.tempId === stepId);
      if (!stepToDelete) {
        return;
      }
      const descendants = workflowChartUtils.getDescendants(
        stepToDelete,
        workflowSteps,
      );
      const toDelete = [...descendants, stepToDelete];
      const updatedSteps = workflowSteps.filter(
        (step) => !toDelete.some((d) => d.tempId === step.tempId),
      );
      setWorkflowSteps([...updatedSteps]);
      setRemovedChartItemId(stepId);
      if (stepToDelete?.parentId) {
        setNextSelectedWorkflowId(stepToDelete?.parentId);
      }
      if (updatedSteps.length === 1) {
        setShowAddStepButton(true);
      }
    };

    const handleStepClick = (id: string) => {
      if (id !== selectedWorkflowId) {
        if (workflowStepRef.current?.save()) {
          saveCurrentStep({ addNewStep: false, nextId: id });
          return true;
        }
        return false;
      }
      return true;
    };

    const handleAddStepClick = () => {
      if (workflowStepRef.current?.save()) {
        saveCurrentStep({
          addNewStep: true,
          parentId: workflowSteps[0].tempId ?? '',
          branch: '',
        });
      }
    };

    const handleAddStepToBranchClick = (
      branchName: string,
      parentId: string,
    ) => {
      if (workflowStepRef.current?.save())
        saveCurrentStep({
          addNewStep: true,
          parentId,
          branch: branchName,
        });
    };

    const handleFormUpdate = () => {
      const currentStep = workflowStepRef.current?.getFormData();
      if (currentStep) {
        setDraftStep({ ...currentStep });
        const sendMessageDraftStep = draftStep as SendMessageActionFormData;
        const sendMessageCurrentStep = currentStep as SendMessageActionFormData;

        if (
          sendMessageDraftStep?.interactiveResponse === true &&
          sendMessageCurrentStep?.interactiveResponse === false
        ) {
          setWorkflowSteps(
            workflowChartUtils.deleteAllDescendants(currentStep, workflowSteps),
          );
        }

        if (
          sendMessageDraftStep?.branches &&
          sendMessageCurrentStep?.branches &&
          !isEqual(
            sendMessageDraftStep.branches,
            sendMessageCurrentStep.branches,
          )
        ) {
          const removed = sendMessageDraftStep.branches.filter(
            (branch) => !sendMessageCurrentStep.branches!.includes(branch),
          );
          const descendants = workflowChartUtils.getDescendants(
            sendMessageDraftStep,
            workflowSteps,
          );
          const toRemove = descendants
            .filter((child) => child.branch && removed.includes(child.branch))
            .map((child) => child.tempId);
          const updatedSteps = workflowSteps.filter(
            (step) => !toRemove.includes(step.tempId),
          );
          setWorkflowSteps(updatedSteps);
        }
      }
    };

    const getTempBranchId = (
      currentStep: SimplifiedWorkflowStepFormData,
      branchName: string,
    ) => `${currentStep.tempId}-${branchName}`;

    const getChildren = (
      node: SimplifiedWorkflowStepFormData,
      allNodes: SimplifiedWorkflowStepFormData[],
    ): SimplifiedWorkflowStepFormData[] => {
      return allNodes.filter((n) => n.parentId === node.tempId);
    };

    const workflowChartItems: WorkflowChartItem[] = workflowSteps
      .map((step, index) => {
        let currentStep = step;
        if (step.tempId === draftStep?.tempId) {
          currentStep = draftStep ?? step;
        }
        const isInteractive = (currentStep as SendMessageActionFormData)
          .interactiveResponse;
        let transitionObjects: WorkflowTransition[] = [];

        const branches =
          (currentStep as SendMessageActionFormData).branches ?? [];

        const children = getChildren(currentStep, workflowSteps);
        const nodeLevel = workflowChartUtils.getNodeLevel(
          currentStep,
          workflowSteps,
        );
        if (isInteractive) {
          const transitions = branches.map((branch) => {
            const branchedStep = workflowSteps.find(
              (step) =>
                step.parentId === currentStep.tempId && step.branch === branch,
            );
            if (branchedStep) {
              return {
                id: branchedStep.tempId ?? '',
                name: branch,
                level: nodeLevel + 1,
              };
            } else {
              return {
                id: getTempBranchId(currentStep, branch),
                name: branch,
                level: nodeLevel + 1,
              };
            }
          });
          transitionObjects.push(...transitions);
        } else {
          if (children && children.length === 1) {
            const nextStep = children[0];
            transitionObjects.push({
              id: nextStep.tempId ?? '',
              level: nodeLevel + 1,
            });
          }
        }
        let col: number | undefined = undefined;
        let totalCols = 1;
        if (currentStep.branch) {
          const ancestor = workflowChartUtils.getFirstAncestorWithBranches(
            currentStep,
            workflowSteps,
          );
          const branchIndex = ancestor?.branches?.findIndex(
            (branch) => branch === currentStep.branch,
          );
          if (!isNil(branchIndex) && branchIndex >= 0) {
            col = branchIndex;
            totalCols = ancestor?.branches?.length ?? 1;
          }
        }

        let chartItems: WorkflowChartItem[] = [
          {
            id: currentStep.tempId ?? '',
            parentId: currentStep.parentId,
            row: nodeLevel,
            colInfo: !isNil(col) ? { col, totalCols } : undefined,
            title: currentStep.title,
            actions: describeSimplifiedWorkflowUtils.getStepDescription(
              currentStep,
              t,
            ),
            onNext: transitionObjects,
          },
        ];
        if (isInteractive) {
          const branchesWithAddButton = branches.filter((branch) => {
            return !workflowSteps.some(
              (step) =>
                step.parentId === currentStep.tempId && step.branch === branch,
            );
          });
          const branchedItems = branchesWithAddButton.map((branch, index) => {
            const branchIndex =
              (currentStep as SendMessageActionFormData)?.branches?.findIndex(
                (br) => br === branch,
              ) ?? 0;
            const totalCols =
              (currentStep as SendMessageActionFormData).branches?.length ?? 1;
            return {
              id: getTempBranchId(currentStep, branch),
              parentId: currentStep.tempId,
              row: nodeLevel + 1,
              colInfo: !isNil(branchIndex)
                ? {
                    col: branchIndex,
                    totalCols,
                  }
                : undefined,
              title: branch,
              actions: [],
              isAddButton: true,
            };
          });
          chartItems.push(...branchedItems);
        } else if (children.length === 0 && nodeLevel > 1) {
          chartItems.push({
            id: getTempBranchId(currentStep, 'add'),
            parentId: currentStep.tempId,
            row: nodeLevel + 1,
            colInfo: !isNil(col) ? { col, totalCols } : undefined,
            title: currentStep.branch ?? '',
            actions: [],
            isAddButton: true,
          });
        }
        [];
        return chartItems;
      })
      .flat();

    let selectedStep: SimplifiedWorkflowStepFormData | undefined;
    if (selectedWorkflowId) {
      selectedStep = workflowSteps.find(
        (step) => step.tempId === selectedWorkflowId,
      );
    }
    if (!selectedStep) {
      selectedStep = simplifiedWorkflowUtils.getDefaultTriggerStep(t);
    }

    return (
      <StyledBox>
        <Stack direction="column" className={classes.container}>
          <Stack direction="row">
            <Box className={classes.chartContainer}>
              <Box className={classes.chart}>
                <MemoizedWorkflowFlowChart
                  items={workflowChartItems}
                  selectedId={selectedWorkflowId}
                  handleStepClick={handleStepClick}
                  handleAddStepClick={handleAddStepClick}
                  handleAddStepToBranchClick={handleAddStepToBranchClick}
                  showAddStepButton={showAddStepButton}
                  removedChartItemId={removedChartItemId}
                />
              </Box>
            </Box>
            <Box className={classes.step}>
              <SimplifiedWorkflowStep
                ref={workflowStepRef}
                stepData={selectedStep}
                handleFormUpdate={handleFormUpdate}
                handleDeleteStep={handleDeleteStep}
              />
            </Box>
          </Stack>
        </Stack>
      </StyledBox>
    );
  },
);

export default memo(EditSimplifiedWorkflow);
